import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import api from '../services/api';

interface DeactivateItemProps {
  open: boolean;
  onClose: () => void;
  selectedItems: any[];
  onComplete: () => void;
}

interface DeactivateItemData {
  store_id: number;
  store_name: string;
  manufacturer_id: number;
  manufacturer_name: string;
  medicine_name: string;
  pack_size: string;
  inventory_quantity: number;
  deactivate: boolean;
}

const DeactivateItem: React.FC<DeactivateItemProps> = ({ open, onClose, selectedItems, onComplete }: DeactivateItemProps) => {
  const [items, setItems] = useState<DeactivateItemData[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open && selectedItems.length > 0) {
      initializeItems();
    }
  }, [open, selectedItems]);

  const initializeItems = () => {
    const initializedItems = selectedItems.map((item: any) => ({
      store_id: item.store_id,
      store_name: item.store_name,
      manufacturer_id: item.manufacturer_id,
      manufacturer_name: item.manufacturer_name,
      medicine_name: item.medicine_name,
      pack_size: item.pack_size,
      inventory_quantity: item.inventory_quantity || 0,
      deactivate: false, // Default to false
    }));

    setItems(initializedItems);
  };

  const handleDeactivateChange = (index: number, value: boolean) => {
    setItems((prev: DeactivateItemData[]) => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], deactivate: value };
      return newItems;
    });
  };

  const handleSubmit = async () => {
    setError(null);
    setSaving(true);

    try {
      const payload = items.map((item: DeactivateItemData) => ({
        store_id: item.store_id,
        manufacturer_id: item.manufacturer_id,
        deactivate: item.deactivate,
      }));

      const response = await api.put('/utilities/item-min-max/bulk-deactivate', { items: payload });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onComplete();
          onClose();
        }, 1500);
      } else {
        setError('Failed to update deactivation status');
      }
    } catch (err: any) {
      console.error('Error updating deactivation:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to update deactivation status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Deactivate Items</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Successfully updated {items.length} items!
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Set the deactivation status for each item. Items marked as "True" will be deactivated.
          </Typography>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Store</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Medicine Name</TableCell>
                <TableCell>Pack Size</TableCell>
                <TableCell align="right">Current Stock</TableCell>
                <TableCell align="center">Deactivate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item: DeactivateItemData, index: number) => (
                <TableRow key={`${item.store_id}_${item.manufacturer_id}`}>
                  <TableCell>{item.store_name}</TableCell>
                  <TableCell>{item.manufacturer_name}</TableCell>
                  <TableCell>{item.medicine_name}</TableCell>
                  <TableCell>{item.pack_size}</TableCell>
                  <TableCell align="right">{item.inventory_quantity}</TableCell>
                  <TableCell align="center">
                    <Select
                      value={item.deactivate}
                      onChange={(e: any) => handleDeactivateChange(index, e.target.value === 'true')}
                      size="small"
                      sx={{ minWidth: 100 }}
                    >
                      <MenuItem value="false">False</MenuItem>
                      <MenuItem value="true">True</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || items.length === 0}
          startIcon={saving && <CircularProgress size={20} />}
        >
          {saving ? 'Saving...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeactivateItem;

